import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { 
  ArrowRight, 
  Store, 
  UtensilsCrossed, 
  CalendarCheck, 
  Package, 
  Shirt, 
  Trophy, 
  Users, 
  Megaphone,
  CheckCircle2
} from "lucide-react";

const SoluzioniPage = () => {
  const location = useLocation();

  // Scroll to anchor when navigating to a specific section
  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.replace('#', '');
      // Small delay to ensure the page has rendered
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          const headerOffset = 100; // Account for fixed header
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // Scroll to top when no hash
      window.scrollTo(0, 0);
    }
  }, [location]);

  const solutions = [
    {
      id: "ristorazione",
      icon: UtensilsCrossed,
      title: "Ristorazione",
      subtitle: "Ristoranti, bar, pizzerie, pub",
      description: "Gestione completa del punto vendita per il settore food & beverage. Dalla comanda alla cassa, dal magazzino ai report.",
      features: [
        "Gestione tavoli e comande",
        "Menu digitale personalizzabile",
        "Integrazione con cucina",
        "Report vendite in tempo reale",
        "Gestione turni e personale"
      ],
      image: "/images/ristorazione.jpg"
    },
    {
      id: "retail",
      icon: Store,
      title: "Retail & Attività Commerciali",
      subtitle: "Negozi, boutique, catene retail",
      description: "Soluzioni complete per il commercio al dettaglio, dalla gestione del singolo negozio alla rete di punti vendita.",
      features: [
        "Gestione inventario multi-sede",
        "Codici a barre e RFID",
        "Fidelity card e promozioni",
        "Analisi vendite per categoria",
        "Integrazione e-commerce"
      ],
      image: "/images/retail.jpg"
    },
    {
      id: "prenotazioni",
      icon: CalendarCheck,
      title: "Gestione Prenotazioni",
      subtitle: "Booking e appuntamenti",
      description: "Sistema avanzato per la gestione di prenotazioni, appuntamenti e risorse, integrato con calendario e notifiche.",
      features: [
        "Calendario interattivo",
        "Prenotazioni online 24/7",
        "Promemoria automatici",
        "Gestione risorse e sale",
        "Storico clienti"
      ],
      image: "/images/prenotazioni.jpg"
    },
    {
      id: "magazzino",
      icon: Package,
      title: "Magazzino & Forniture",
      subtitle: "Inventario e logistica",
      description: "Controllo completo di magazzino, ordini fornitori e movimentazione merce con alert automatici e report dettagliati.",
      features: [
        "Inventario in tempo reale",
        "Alert scorte minime",
        "Ordini fornitori automatici",
        "Tracciabilità lotti",
        "Report movimentazione"
      ],
      image: "/images/magazzino.jpg"
    },
    {
      id: "lavanderia",
      icon: Shirt,
      title: "Gestione Lavanderia",
      subtitle: "Lavanderie e tintorie",
      description: "Software dedicato per lavanderie con tracciamento capi, gestione clienti e fatturazione automatica.",
      features: [
        "Etichettatura capi",
        "Tracciamento stato lavaggio",
        "Gestione clienti business",
        "Fatturazione ricorrente",
        "Notifiche ritiro"
      ],
      image: "/images/lavanderia.jpg"
    },
    {
      id: "sport",
      icon: Trophy,
      title: "Società Sportive",
      subtitle: "ASD e associazioni",
      description: "Gestione completa per società sportive dilettantistiche: tesserati, quote, eventi e comunicazioni.",
      features: [
        "Anagrafica tesserati",
        "Gestione quote e pagamenti",
        "Calendario eventi",
        "Comunicazioni massive",
        "Report federali"
      ],
      image: "/images/sport.jpg"
    },
    {
      id: "hr",
      icon: Users,
      title: "Gestione Risorse Umane",
      subtitle: "Personale e turni",
      description: "Modulo HR integrato per la gestione del personale: presenze, turni, ferie e documentazione.",
      features: [
        "Gestione presenze",
        "Pianificazione turni",
        "Richieste ferie online",
        "Documentazione digitale",
        "Report ore lavorate"
      ],
      image: "/images/hr.jpg"
    },
    {
      id: "promozioni",
      icon: Megaphone,
      title: "Promozioni Commerciali",
      subtitle: "Marketing e fidelizzazione",
      description: "Strumenti per creare e gestire campagne promozionali, sconti, coupon e programmi fedeltà.",
      features: [
        "Creazione promozioni",
        "Coupon e sconti",
        "Programmi punti",
        "Analisi efficacia",
        "Segmentazione clienti"
      ],
      image: "/images/promozioni.jpg"
    }
  ];

  return (
    <div data-testid="soluzioni-page">
      {/* Hero Section */}
      <section className="bg-[#f5f6fa] section-padding" data-testid="solutions-hero">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <span className="badge mb-6">Le Nostre Soluzioni</span>
            <h1 className="heading-1 text-[#1a1f36] mb-8">
              Una piattaforma, infinite possibilità
            </h1>
            <p className="body-large max-w-3xl mx-auto">
              iSiAP si adatta alle esigenze specifiche di ogni settore. Scopri la soluzione 
              perfetta per la tua attività.
            </p>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="section-padding" data-testid="solutions-grid">
        <div className="section-container">
          <div className="space-y-20">
            {solutions.map((solution, index) => (
              <div 
                key={solution.id}
                id={solution.id}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`solution-${solution.id}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="icon-container-lg">
                      <solution.icon size={28} />
                    </div>
                    <div>
                      <h2 className="heading-3 text-[#1a1f36]">{solution.title}</h2>
                      <p className="text-[#7a7f94] text-sm">{solution.subtitle}</p>
                    </div>
                  </div>
                  
                  <p className="text-[#4a5068] text-lg leading-relaxed mb-8">
                    {solution.description}
                  </p>

                  <ul className="space-y-3 mb-8">
                    {solution.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 text-[#4a5068]">
                        <CheckCircle2 size={18} className="text-[#FF6B00] shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Link 
                    to="/prenota" 
                    className="btn-primary inline-flex"
                    data-testid={`solution-cta-${solution.id}`}
                  >
                    Richiedi Demo
                    <ArrowRight size={18} />
                  </Link>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl shadow-[#1a1f36]/10">
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Navy */}
      <section className="bg-[#1a1f36] section-padding" data-testid="solutions-cta">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Non trovi la soluzione che cerchi?
            </h2>
            <p className="text-[#9ca3af] text-lg mb-8">
              iSiAP è una piattaforma modulare e personalizzabile. 
              Contattaci per discutere le tue esigenze specifiche.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contatti" 
                className="inline-flex items-center gap-2 bg-[#FF6B00] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#E55F00] transition-colors"
                data-testid="solutions-final-cta"
              >
                Parliamone Insieme
                <ArrowRight size={18} />
              </Link>
              <a 
                href="tel:0383644311" 
                className="inline-flex items-center gap-2 border-2 border-[#4a5068] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#2d3452] transition-colors"
              >
                Chiamaci: 0383 644311
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SoluzioniPage;
